/**
 * Design Tokens: Spacing
 *
 * 8px base grid, moderate radii matching the Neuronix dashboard style.
 * Clean shadows, comfortable component spacing.
 */

export const spacing = {
  base: 8,
  xxs: '2px',

  scale: {
    0: '0px',
    0.5: '2px',
    1: '4px',
    1.5: '6px',
    2: '8px',
    2.5: '10px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    9: '36px',
    10: '40px',
    12: '48px',
    14: '56px',
    16: '64px',
    20: '80px',
    24: '96px',
    32: '128px',
  },

  component: {
    cardPadding: '24px',
    cardMargin: '16px',
    cardGap: '20px',

    gridGap: '20px',
    gridGapLarge: '24px',

    sectionGap: '32px',
    sectionPadding: '32px',

    formFieldGap: '16px',
    formGroupGap: '24px',

    buttonPadding: '10px 20px',
    buttonPaddingSmall: '6px 14px',
    buttonPaddingLarge: '12px 28px',
    buttonGap: '8px',

    navItemPadding: '8px 16px',
    navItemGap: '4px',

    headerPadding: '12px 24px',
    headerHeight: '56px',

    sidebarWidth: '260px',
    sidebarPadding: '16px',
  },

  borderRadius: {
    none: '0px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    xxl: '20px',
    full: '9999px',

    card: '12px',
    button: '8px',
    buttonPill: '9999px',
    input: '8px',
    badge: '6px',
    avatar: '50%',
    navPill: '20px',
    chip: '16px',
  },

  shadow: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.03)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.05)',
    md: '0 2px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.08)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.10)',

    card: '0 1px 3px rgba(0, 0, 0, 0.04)',
    cardHover: '0 4px 12px rgba(0, 0, 0, 0.08)',
    dropdown: '0 4px 16px rgba(0, 0, 0, 0.12)',
    modal: '0 16px 48px rgba(0, 0, 0, 0.16)',
  },

  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },

  transitions: {
    fast: '0.15s',
    normal: '0.25s',
    slow: '0.4s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Shorthand semantic spacing (MUI multiplier values)
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
  section: 8,

  containers: {
    xs: '320px',
    sm: '480px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    xxl: '1440px',
  },
} as const

export const getSpacing = (value: keyof typeof spacing.scale): string => {
  return spacing.scale[value]
}

export const getComponentSpacing = (component: keyof typeof spacing.component): string => {
  return spacing.component[component]
}

export const getBorderRadius = (size: keyof typeof spacing.borderRadius): string => {
  return spacing.borderRadius[size]
}

export const spacingMixin = {
  p: (value: keyof typeof spacing.scale) => `padding: ${spacing.scale[value]};`,
  px: (value: keyof typeof spacing.scale) => `padding-left: ${spacing.scale[value]}; padding-right: ${spacing.scale[value]};`,
  py: (value: keyof typeof spacing.scale) => `padding-top: ${spacing.scale[value]}; padding-bottom: ${spacing.scale[value]};`,
  m: (value: keyof typeof spacing.scale) => `margin: ${spacing.scale[value]};`,
  mx: (value: keyof typeof spacing.scale) => `margin-left: ${spacing.scale[value]}; margin-right: ${spacing.scale[value]};`,
  my: (value: keyof typeof spacing.scale) => `margin-top: ${spacing.scale[value]}; margin-bottom: ${spacing.scale[value]};`,
  gap: (value: keyof typeof spacing.scale) => `gap: ${spacing.scale[value]};`,
  rounded: (size: keyof typeof spacing.borderRadius) => `border-radius: ${spacing.borderRadius[size]};`,
}

export default spacing
